import React from 'react';
import {
  Alert, Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, IconButton,
  InputLabel, List, ListItem, ListItemText, MenuItem, Radio, RadioGroup, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { FichierReponse, Question, ValeurReponse, decrireFichiers } from '../services/formulairesServices';

// Rendu d'une question telle que le client la verra (9 types, MUI v5), pour le Dialog « Tester le formulaire ».
// État contrôlé : `valeur` (chaîne, ou tableau de chaînes pour choix_multiple) et `fichiers` (liste de la question)
// appartiennent au parent. Rien n'est téléversé : une question fichier propose « Simuler un fichier joint », qui
// ajoute une entrée fictive à l'état local du test (le moteur ne regarde que la présence et le nombre de fichiers).
// L'aide est rendue en helperText, l'obligation par l'astérisque MUI (`required`), l'erreur de `valider` en ligne.

export interface QuestionRenduAdminProps {
  question: Question;
  valeur: ValeurReponse | undefined;          // undefined = pas encore répondu
  fichiers: FichierReponse[];                 // fichiers (simulés) de cette question
  erreur?: string;                            // message de valider() pour cette question
  onChange: (valeur: ValeurReponse) => void;
  onFichiersChange: (fichiers: FichierReponse[]) => void;
}

// Au-delà de ce nombre d'options, un choix unique passe du RadioGroup au Select (plan §3.2)
const MAX_OPTIONS_RADIO = 5;

// Entrée fictive : simulation-<n>.pdf, n = 1 + plus grand numéro déjà présent (noms uniques même après un retrait)
const fichierSimule = (existants: FichierReponse[]): FichierReponse => {
  const n = existants.reduce((max, f) => {
    const m = /^simulation-(\d+)\.pdf$/.exec(f.nom);
    return m ? Math.max(max, Number(m[1])) : max;
  }, 0) + 1;
  const nom = `simulation-${n}.pdf`;
  return { nom, nom_original: nom, taille: 1234, date: new Date().toISOString() };
};

const QuestionRenduAdmin: React.FC<QuestionRenduAdminProps> = ({ question: q, valeur, fichiers, erreur, onChange, onFichiersChange }) => {
  const texte = typeof valeur === 'string' ? valeur : '';
  const liste = Array.isArray(valeur) ? valeur : [];
  const aide = q.aide || undefined;
  const helper = erreur || aide;
  const options = q.options || [];

  // Paragraphe d'information : jamais de réponse
  if (q.type === 'info') {
    return (
      <Alert severity="info" icon={false}>
        <Typography variant="body2">{q.libelle}</Typography>
        {aide && <Typography variant="caption" color="text.secondary" display="block">{aide}</Typography>}
      </Alert>
    );
  }

  switch (q.type) {
    case 'texte':
    case 'texte_long':
      return (
        <TextField
          label={q.libelle}
          required={q.obligatoire}
          fullWidth
          size="small"
          type={q.type === 'texte' && q.format === 'email' ? 'email' : q.type === 'texte' && q.format === 'telephone' ? 'tel' : 'text'}
          multiline={q.type === 'texte_long'}
          minRows={q.type === 'texte_long' ? 3 : undefined}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          error={!!erreur}
          helperText={helper}
        />
      );

    case 'nombre':
      // La valeur reste une chaîne (comme dans les réponses) : c'est valider() qui contrôle le format et les bornes
      return (
        <TextField
          label={q.libelle}
          required={q.obligatoire}
          fullWidth
          size="small"
          type="number"
          inputProps={{ min: q.min, max: q.max, step: 'any' }}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          error={!!erreur}
          helperText={helper}
        />
      );

    case 'date':
      return (
        <TextField
          label={q.libelle}
          required={q.obligatoire}
          fullWidth
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          error={!!erreur}
          helperText={helper}
        />
      );

    case 'oui_non':
      return (
        <FormControl component="fieldset" required={q.obligatoire} error={!!erreur} fullWidth>
          <FormLabel component="legend">{q.libelle}</FormLabel>
          <RadioGroup row value={texte} onChange={(e) => onChange(e.target.value)}>
            <FormControlLabel value="oui" control={<Radio size="small" />} label="Oui" />
            <FormControlLabel value="non" control={<Radio size="small" />} label="Non" />
          </RadioGroup>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );

    case 'choix':
      if (options.length <= MAX_OPTIONS_RADIO) {
        return (
          <FormControl component="fieldset" required={q.obligatoire} error={!!erreur} fullWidth>
            <FormLabel component="legend">{q.libelle}</FormLabel>
            <RadioGroup value={texte} onChange={(e) => onChange(e.target.value)}>
              {options.map((o) => (
                <FormControlLabel key={o.valeur} value={o.valeur} control={<Radio size="small" />} label={o.libelle || o.valeur} />
              ))}
            </RadioGroup>
            {helper && <FormHelperText>{helper}</FormHelperText>}
          </FormControl>
        );
      }
      return (
        <FormControl size="small" required={q.obligatoire} error={!!erreur} fullWidth>
          <InputLabel id={`${q.id}-label`}>{q.libelle}</InputLabel>
          <Select
            labelId={`${q.id}-label`}
            label={q.libelle}
            value={texte}
            onChange={(e) => onChange(String(e.target.value))}
          >
            <MenuItem value=""><em>Aucune réponse</em></MenuItem>
            {options.map((o) => (
              <MenuItem key={o.valeur} value={o.valeur}>{o.libelle || o.valeur}</MenuItem>
            ))}
          </Select>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );

    case 'choix_multiple': {
      const basculer = (v: string, coche: boolean) =>
        onChange(coche ? [...liste.filter((x) => x !== v), v] : liste.filter((x) => x !== v));
      return (
        <FormControl component="fieldset" required={q.obligatoire} error={!!erreur} fullWidth>
          <FormLabel component="legend">{q.libelle}</FormLabel>
          <FormGroup>
            {options.map((o) => (
              <FormControlLabel
                key={o.valeur}
                control={<Checkbox size="small" checked={liste.indexOf(o.valeur) >= 0} onChange={(e) => basculer(o.valeur, e.target.checked)} />}
                label={o.libelle || o.valeur}
              />
            ))}
          </FormGroup>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );
    }

    case 'fichier': {
      const max = q.max_fichiers || 1;
      const plein = fichiers.length >= max;
      return (
        <FormControl component="fieldset" required={q.obligatoire} error={!!erreur} fullWidth>
          <FormLabel component="legend">{q.libelle}</FormLabel>
          {fichiers.length > 0 && (
            <List dense disablePadding>
              {fichiers.map((f) => (
                <ListItem
                  key={f.nom}
                  disableGutters
                  secondaryAction={
                    <Tooltip title="Retirer">
                      <IconButton edge="end" size="small" onClick={() => onFichiersChange(fichiers.filter((x) => x.nom !== f.nom))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText primary={f.nom_original} secondary={`${Math.round(f.taille / 1024)} ko — simulation`} />
                </ListItem>
              ))}
            </List>
          )}
          <Box sx={{ mt: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              disabled={plein}
              onClick={() => onFichiersChange([...fichiers, fichierSimule(fichiers)])}
            >
              Simuler un fichier joint
            </Button>
          </Box>
          <FormHelperText>
            {erreur || `${aide ? `${aide} — ` : ''}${decrireFichiers(q)} — aucun fichier n'est téléversé en mode test`}
          </FormHelperText>
        </FormControl>
      );
    }

    default:
      return null;
  }
};

export default QuestionRenduAdmin;
